import { Injectable } from "@angular/core";
import { HttpClientService } from "./http-client.service";
import { from, Observable, throwError } from "rxjs";
import { catchError, shareReplay, switchMap, tap } from "rxjs/operators";

@Injectable()
export class DomiciliationProcessService {
  constructor(private http: HttpClientService) {}

  // GET /api/domiciliation_process/check_duplicated_document
  private checkDuplicatedCache = new Map<string, Observable<any>>();

  private async hashString(value: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(value);

    const hashBuffer = await crypto.subtle.digest("SHA-256", data);

    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }

  clearCache() {
    this.checkDuplicatedCache.clear();
  }

  clearCacheForHash(hash: string) {
    this.checkDuplicatedCache.delete(hash);
  }

  public checkDuplicated(content: string): Observable<any> {
    return from(this.hashString(content)).pipe(
      switchMap((hash) => {
        if (this.checkDuplicatedCache.has(hash)) {
          return this.checkDuplicatedCache
            .get(hash)!
            .pipe(tap((_) => console.log("DomiciliationProcessService.checkDuplicated: Ok (from cache).")));
        }

        const options = {
          content,
        };
        const obs$ = this.http.post("/api/domiciliation_process/check_duplicated_document", options).pipe(
          tap((_) => console.log("DomiciliationProcessService.checkDuplicated: Ok. (Cached by hash)")),
          shareReplay(1),
          catchError((err) => {
            this.checkDuplicatedCache.delete(hash);
            return throwError(() => err);
          })
        );
        this.checkDuplicatedCache.set(hash, obs$);
        return obs$;
      })
    );
  }

  public getDomiProcess({ date = undefined }): Observable<any> {
    let options = {};
    date && (options = { date });
    return this.http
      .get("/api/domiciliation_process", options)
      .pipe(tap((_) => console.log("DomiciliationProcessService.getDomiProcess: Ok.")));
  }

  public getAttempts({ date = undefined, completed = undefined }): Observable<any> {
    let options = { date, completed };
    return this.http
      .get("/api/domiciliation_process/attempts", { params: options })
      .pipe(tap((_) => console.log("DomiciliationProcessService.getAttempts: Ok.")));
  }

  public saveAttemptDetails(id, investors_detail, layouts_count): Observable<any> {
    const options = { investors_detail, layouts_count };
    return this.http
      .put(`/api/domiciliation_attempt/${id}/save_details`, options)
      .pipe(tap((_) => console.log("DomiciliationProcessService.saveAttemptDetails: Ok.")));
  }

  public deleteAttempt(attempt_id: number): Observable<any> {
    return this.http
      .delete(`/api/domiciliation_attempt/${attempt_id}`, {})
      .pipe(tap((_) => console.log("DomiciliationProcessService.deleteAttempt: Ok.")));
  }

  public finishAttempt(
    id,
    domi_bank_full_collection_count,
    domi_bank_partial_collection_count,
    others_done_collection_count,
    others_lost_collection_count,
    domi_bank_total_collected,
    others_banks_total_collected
  ): Observable<any> {
    const options = {
      id,
      domi_bank_full_collection_count,
      domi_bank_partial_collection_count,
      others_done_collection_count,
      others_lost_collection_count,
      domi_bank_total_collected,
      others_banks_total_collected,
    };
    return this.http
      .post(`/api/domiciliation_attempt/${id}/finish`, options)
      .pipe(tap((_) => console.log("DomiciliationProcessService.finishAttempt: Ok.")));
  }

  public reasonsChange(reasons): Observable<any> {
    const options = { reasons };
    return this.http
      .post(`/api/domiciliation_process/save_reasons_change`, options)
      .pipe(tap((_) => console.log("DomiciliationProcessService.reasonsChange: Ok.")));
  }

  public downloadResults(domi_process_id): Observable<any> {
    return this.http
      .get(`/api/domiciliation_process/${domi_process_id}/download_results_zip`, { responseType: "blob" as "json" })
      .pipe(tap((_) => console.log("DomiciliationProcessService.downloadResults: Ok.")));
  }

  public downloadResultsByDate(domi_date): Observable<any> {
    const options = { domi_date };
    return this.http
      .get(`/api/domiciliation_process/download_zip`, { params: options, responseType: "blob" as "json" })
      .pipe(tap((_) => console.log("DomiciliationProcessService.downloadResultsByDate: Ok.")));
  }

  public downloadReport(domi_date): Observable<any> {
    const options = { domi_date };
    return this.http
      .get(`/api/domiciliation_process/download_report`, { params: options, responseType: "blob" as "json" })
      .pipe(tap((_) => console.log("DomiciliationProcessService.downloadReport: Ok.")));
  }

  public addTab(id): Observable<any> {
    return this.http
      .post(`/api/domiciliation_process/${id}/new_tab`, {})
      .pipe(tap((_) => console.log("DomiciliationProcessService.addTab: Ok.")));
  }

  public uploadFiles(id, files: any[]): Observable<any> {
    return this.http
      .post(`/api/domiciliation_attempt/${id}/upload_files`, { files })
      .pipe(tap((_) => console.log("DomiciliationProcessService.uploadFiles: Ok.")));
  }

  public nextDomiDate(date: null) {
    return this.http
      .get(`/api/domiciliation_process/next_domi_date`, { params: { date } })
      .pipe(tap((_) => console.log("PaymentsService.nextDomiDate: Ok.")));
  }

  public checkStatus(id: number): Observable<any> {
    return this.http
      .get(`/api/domiciliation_process/${id}/check_status`, {})
      .pipe(tap((_) => console.log("DomiciliationProcessService.checkStatus: Ok.")));
  }
}
